import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';

import {
  Answer,
  Question,
} from 'src/app/feature/onboarding-v2/models/question.model';
import { QuestionItemComponent } from '../question-item/question-item.component';
import { AnalyticsService } from '../../services/analytics.service';
import { ErrorModalService } from '../../services/error-modal-service.service';
import { LoadingService } from '../../services/loading.service';
import { QuizModalService } from '../../services/quiz-modal.service';
import { UserQuizService } from '../../services/user-quiz.service';
import { ValidationModalsService } from '../../services/validation-modals.service';
import { UserValidationStateStore } from '../../state/user-validation-state.store';

/**
 * Modal full-screen del cuestionario "knowledge" que se abre cuando el backend
 * responde CU010 al intentar operar y el usuario eligió "Empezar ahora".
 *
 * Comparte la estructura del modal de notificaciones (sheet anclado debajo del
 * header) pero ahora con el contenido del Quiz: lista de `Question[]`
 * renderizadas como accordion con radios, y un CTA "Finalizar" sticky abajo.
 */
@Component({
  selector: 'app-quiz-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonModule, QuestionItemComponent],
  templateUrl: './quiz-modal.component.html',
  styleUrls: ['./quiz-modal.component.scss'],
})
export class QuizModalComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly quizModalService = inject(QuizModalService);
  private readonly userQuizService = inject(UserQuizService);
  private readonly userValidationStateStore = inject(UserValidationStateStore);
  private readonly validationModalsService = inject(ValidationModalsService);
  private readonly loadingService = inject(LoadingService);
  private readonly errorModalService = inject(ErrorModalService);
  private readonly messageService = inject(MessageService);
  private readonly analyticsService = inject(AnalyticsService);

  readonly isOpen = this.quizModalService.isOpen;

  readonly questions = signal<Question[]>([]);
  readonly answers = signal<Record<string, string>>({});
  readonly expandedId = signal<string | null>(null);
  readonly loading = signal<boolean>(false);
  readonly submitting = signal<boolean>(false);

  /** Cuestionario habilitado solo cuando todas las preguntas tienen respuesta. */
  readonly canSubmit = computed(() => {
    const qs = this.questions();
    const ans = this.answers();
    if (qs.length === 0) {
      return false;
    }
    return qs.every((q) => !!ans[q.questionId]) && !this.submitting();
  });

  readonly answersMap = computed(() => {
    const map = new Map<string, string>();
    Object.entries(this.answers()).forEach(([qid, oid]) => map.set(qid, oid));
    return map;
  });

  constructor() {
    // Cuando el modal pasa a abierto, cargamos (o recargamos) las preguntas.
    // Cuando se cierra reseteamos para que la próxima apertura sea limpia.
    effect(() => {
      const open = this.isOpen();
      if (open) {
        this.loadQuestions();
      } else {
        this.resetState();
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Interacción usuario
  // ─────────────────────────────────────────────────────────────────────────

  onClose(): void {
    this.quizModalService.close();
  }

  isExpanded(questionId: string): boolean {
    return this.expandedId() === questionId;
  }

  selectedOptionFor(questionId: string): string | null {
    return this.answersMap().get(questionId) ?? null;
  }

  onToggle(questionId: string): void {
    this.expandedId.update((current) =>
      current === questionId ? null : questionId,
    );
  }

  onSelection(questionId: string, optionId: string): void {
    this.answers.update((current) => ({ ...current, [questionId]: optionId }));
    this.collapseAndAdvance(questionId);
  }

  onSubmit(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.analyticsService.trackVirtualEvent({
      tipoElemento: 'Botón',
      zona: 'Wallet',
      subZona: 'Validación pendiente',
      seccion: 'Cuestionario',
      cta: 'Finalizar',
      pageTitle: 'Wallet - Cuestionario knowledge',
    });

    const payload: Answer[] = this.questions().map((q) => ({
      idQuestion: q.questionId,
      idOption: this.answers()[q.questionId],
    }));

    this.submitting.set(true);
    this.loadingService.show();

    this.userQuizService
      .submitAnswers(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);

          if (!res.success || !res.approved) {
            this.loadingService.hide();
            this.analyticsService.trackEvent(
              'quiz_submit_rejected',
              'wallet',
              res.success ? 'not_approved' : 'success_false',
            );
            // Loop: cerramos el modal y avisamos al usuario para que
            // pueda volver a intentarlo al reintentar comprar.
            this.onClose();
            this.showQuizRejectedToast();
            return;
          }

          this.analyticsService.trackEvent(
            'quiz_submit_approved',
            'wallet',
            'approved',
          );
          this.fetchValidationStatusAndNotify();
        },
        error: (err: HttpErrorResponse) => {
          this.submitting.set(false);
          this.loadingService.hide();
          this.analyticsService.trackEvent(
            'quiz_submit_error',
            'wallet',
            `${err.status}_${err.error?.code ?? 'unknown'}`,
          );
          this.errorModalService.show(err.error);
        },
      });
  }

  private showQuizRejectedToast(): void {
    this.messageService.add({
      key: 'quiz-rejected',
      severity: 'warn',
      summary: 'No aprobaste el cuestionario.',
      detail: 'Inténtalo nuevamente.',
      sticky: false,
      life: 6000,
    });
  }

  trackByQuestionId(_: number, question: Question): string {
    return question.questionId;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.onClose();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Internos
  // ─────────────────────────────────────────────────────────────────────────

  private loadQuestions(): void {
    this.loading.set(true);
    this.loadingService.show();

    this.userQuizService
      .getQuestions('knowledge')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          this.loadingService.hide();

          if (!res.success || !res.questions?.length) {
            this.errorModalService.show({
              code: 'QZ_001',
              description: 'No pudimos cargar el cuestionario',
              detail: ['Intenta nuevamente en unos minutos.'],
            });
            this.onClose();
            return;
          }

          this.questions.set(res.questions);
          this.answers.set({});
          this.expandedId.set(res.questions[0].questionId);
        },
        error: (err: HttpErrorResponse) => {
          this.loading.set(false);
          this.loadingService.hide();
          this.errorModalService.show(
            err.error ?? {
              code: 'QZ_002',
              description: 'No pudimos cargar el cuestionario',
              detail: ['Intenta nuevamente en unos minutos.'],
            },
          );
          this.onClose();
        },
      });
  }

  /**
   * Tras un envío aprobado, consultamos el estado consolidado vía el store
   * (que internamente llama `/user/validation-status`) y mostramos el modal
   * de feedback adecuado según backofficeStatus / fullyValidated.
   */
  private fetchValidationStatusAndNotify(): void {
    this.userValidationStateStore
      .refresh()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loadingService.hide();
          this.onClose();
          this.showResultModalFromState();
        },
        error: (err: HttpErrorResponse) => {
          this.loadingService.hide();
          this.onClose();
          this.analyticsService.trackEvent(
            'quiz_validation_status_error',
            'wallet',
            `${err.status}_${err.error?.code ?? 'unknown'}`,
          );
          // Si la consulta de estado falla, asumimos el caso conservador:
          // el quiz fue aprobado pero aún no sabemos sobre backoffice.
          this.validationModalsService.showOnTheRightTrackModal();
        },
      });
  }

  private showResultModalFromState(): void {
    if (this.userValidationStateStore.effectiveFullyValidated()) {
      this.validationModalsService.showReadyToBuyModal();
      return;
    }
    // Considera rechazo tanto vía backofficeStatus como vía userStatus ('I'/'R').
    if (this.userValidationStateStore.isRejected()) {
      this.validationModalsService.showRejectedModal();
      return;
    }
    this.validationModalsService.showOnTheRightTrackModal();
  }

  /**
   * Cierra la pregunta actual y, si queda alguna sin responder, expande la
   * siguiente para acompañar el flujo natural del cuestionario.
   */
  private collapseAndAdvance(currentId: string): void {
    const qs = this.questions();
    const ans = this.answers();
    const nextUnanswered = qs.find(
      (q) => q.questionId !== currentId && !ans[q.questionId],
    );
    this.expandedId.set(nextUnanswered ? nextUnanswered.questionId : null);
  }

  private resetState(): void {
    this.questions.set([]);
    this.answers.set({});
    this.expandedId.set(null);
    this.loading.set(false);
    this.submitting.set(false);
  }
}
